import { z } from 'zod';
/**
 * Top-N plugin install row. `installCount` is the wire-friendly rename for
 * the storage column `analytics_daily_installs.distinct_ip_count` summed
 * over the trailing window — privacy-preserving by construction (we never
 * store full IPs; the rollup dedups by `/24` IPv4 prefixes per spec §7).
 */
export const metricInstallSchema = z.object({
    pluginId: z.string().uuid(),
    pluginName: z.string(),
    installCount: z.number().int().min(0),
});
/**
 * Per-team activity row. Both counters are summed over the requested window
 * from `analytics_daily_team_activity`. `teamDisplayName` falls back to
 * `teamName` server-side so the UI can render a single label without an
 * extra join.
 */
export const metricTeamActivitySchema = z.object({
    teamId: z.string().uuid(),
    teamName: z.string(),
    teamDisplayName: z.string(),
    submissionCount: z.number().int().min(0),
    activeUserCount: z.number().int().min(0),
});
/**
 * One caveat about a number on the page, keyed so the UI can order or
 * suppress individual notes without string-matching prose. Text lives in the
 * API rather than the UI so a CSV consumer reads the same caveats the page
 * shows (design §5.3).
 */
export const metricNoteSchema = z.object({
    key: z.string(),
    text: z.string(),
});
/**
 * Snapshot of plugin status counts. Status is COMPUTED from
 * (deprecated_at, yanked_at) at query time — yanked takes precedence over
 * deprecated, mirroring `computePluginStatus` in plugins/service.ts.
 * `totalPlugins` excludes soft-deleted rows (`deleted_at IS NULL`).
 */
export const metricSummarySchema = z.object({
    totalPlugins: z.number().int().min(0),
    activePlugins: z.number().int().min(0),
    deprecatedPlugins: z.number().int().min(0),
    yankedPlugins: z.number().int().min(0),
    /** Plugins currently listed in the Hub (`hub_listed_at IS NOT NULL`). */
    pluginsListed: z.number().int().min(0),
    /** Teams provisioned (`teams`, not soft-deleted). */
    teams: z.number().int().min(0),
    /** Teams with at least one non-deleted plugin. */
    teamsPublishing: z.number().int().min(0),
    /** Users who have ever signed in, excluding the seeded system actor and
     *  soft-deleted rows. */
    usersTotal: z.number().int().min(0),
    usersActive7d: z.number().int().min(0),
    usersActive30d: z.number().int().min(0),
    usersActive90d: z.number().int().min(0),
    /** Distinct non-system contributors in the trailing 90 days. A CEILING —
     *  see the `contributors-ceiling` note. */
    contributors90d: z.number().int().min(0),
    commits90d: z.number().int().min(0),
    /**
     * How current the contribution numbers above are: the OLDEST
     * `plugin_sources.contributions_synced_through` watermark across the
     * sources the `collect-contributions` worker considers eligible (the
     * worker's own set — `status = 'active' AND deleted_at IS NULL AND
     * team_id IS NOT NULL AND ado_repo_id IS NOT NULL`). Oldest, not newest,
     * because a number is only as fresh as its least-collected input.
     *
     * **Computed over a DIFFERENT POPULATION from the numbers it qualifies.**
     * The contribution aggregates (`contributors90d`, `commits90d`, and every
     * per-team and time-series figure) filter only `is_system = false` plus
     * the date window — they never join `plugin_sources`. A repo's historical
     * `analytics_daily_contributions` rows therefore stay in every commit,
     * contributor and by-team total after its source row is deactivated or
     * soft-deleted, while that row drops out of THIS field's population. So
     * deactivating the least-recently-collected repo moves this value FORWARD
     * without a single new commit having been collected. Read it as "how
     * up-to-date the collector is", never as "the numbers cover exactly these
     * rows".
     *
     * `null` means NOTHING HAS BEEN COLLECTED from any eligible source, and
     * `sourcesEligible` says which of two very different reasons that is.
     * `> 0`: the collector has never had a successful first run anywhere —
     * every contribution figure here is a structural zero, not a measured one
     * (the `contributions-never-collected` note). `0`: there is nothing to
     * collect at all — no nightly run will ever change these numbers, so
     * promising one is wrong (the `contributions-no-eligible-sources` note).
     *
     * A NON-NULL value is not self-certifying either. Compare its age against
     * `contributionsStaleAfterHours`: past that the collector has stopped for
     * at least one repository, and the API attaches `contributions-stale`. The
     * numbers then describe only the days up to this instant — everything
     * since is unmeasured, not quiet.
     *
     * Timestamp, not a date: the column is `timestamptz` (the worker stores
     * its run-start instant), so unlike
     * `metricTeamContributionsSchema.lastCommitDate` there IS a time-of-day to
     * report.
     */
    contributionsCollectedThrough: z.string().datetime().nullable(),
    /**
     * How many `plugin_sources` rows the `collect-contributions` worker would
     * walk on its next run: the denominator for `sourcesNeverCollected` and
     * the population `contributionsCollectedThrough` is computed over.
     *
     * On the wire because `0` beside a `null` watermark is an internally
     * contradictory-looking pair that nothing else on this payload
     * disambiguates. The freshness aggregate has no `GROUP BY`, so with no
     * eligible rows it returns a single row that is IDENTICAL, in the
     * watermark and never-collected fields, to "26 eligible sources and the
     * collector has never run" — `(null, 0)`. Without this count a consumer
     * renders the first state as the second and promises a nightly run that
     * cannot change the numbers, so an eligibility or provisioning bug (a
     * `status` flip, a NULL `ado_repo_id`) presents as a collector warming up.
     */
    sourcesEligible: z.number().int().min(0),
    /**
     * How many of those same eligible sources have a NULL watermark — never
     * collected even once. Lets a surface label the caveat with its true blast
     * radius ("3 of 26 sources") from the payload rather than recomputing the
     * collector's eligibility rules client-side, the same reason
     * `hubQueueResponseSchema.longOpenThresholdHours` travels beside
     * `longOpenPrCount` (`endpoints/admin.ts`).
     *
     * Always `<= sourcesEligible`, and equal to it exactly when
     * `contributionsCollectedThrough` is `null` and `sourcesEligible > 0`.
     *
     * `0` is NOT on its own the healthy state. It says only that every
     * eligible source has had a first run at some point — equally true of a
     * collector that froze three weeks ago — and it is trivially `0` when
     * `sourcesEligible` is `0` and there is nothing to collect. Healthy is
     * `sourcesEligible > 0` AND `sourcesNeverCollected === 0` AND
     * `contributionsCollectedThrough` no older than
     * `contributionsStaleAfterHours`; equivalently, no `contributions-`
     * freshness note in `notes`.
     */
    sourcesNeverCollected: z.number().int().min(0),
    /**
     * The age (in hours) past which `contributionsCollectedThrough` is treated
     * as stale and the `contributions-stale` note is attached — sourced from
     * `CONTRIBUTIONS_STALE_AFTER_HOURS` in
     * `marketplace-api/src/modules/metrics/service.ts`, which derives it from
     * the collector's `0 1 * * *` cadence plus headroom for one slow run.
     *
     * On the wire for exactly the reason
     * `hubQueueResponseSchema.longOpenThresholdHours` is: a surface that wants
     * to label its own caveat ("collected 21 days ago; expected within 26
     * hours") must read the boundary the note was computed against rather than
     * keep a second copy of the number, which silently disagrees with the API
     * the first time the collector's schedule changes.
     */
    contributionsStaleAfterHours: z.number().int().positive(),
    /**
     * Caveats that belong to the DATA, not the page. Served from the API so
     * every consumer (the page, a future CLI, an exported CSV reader) states
     * the same thing about the same numbers.
     */
    notes: z.array(metricNoteSchema),
});
/**
 * One time-series bucket of user activity. `periodStart` is the bucket's
 * first day (`date_trunc(grain, …)`, ISO date). `activeUsers` counts users
 * whose `last_login_at` falls in the bucket; for buckets predating the
 * `last_login_at` deploy the API falls back to `updated_at` and says so via
 * the `active-users-fallback` note.
 */
export const metricUsersPointSchema = z.object({
    periodStart: z.string().date(),
    newUsers: z.number().int().min(0),
    cumulativeUsers: z.number().int().min(0),
    activeUsers: z.number().int().min(0),
});
/**
 * One time-series bucket of contribution activity, `is_system = false` only.
 * `contributors` is `COUNT(DISTINCT COALESCE(user_id::text, author_email))` —
 * a ceiling, because a person committing under an unresolvable email counts
 * separately.
 */
export const metricContributionsPointSchema = z.object({
    periodStart: z.string().date(),
    commits: z.number().int().min(0),
    mergedPrs: z.number().int().min(0),
    contributors: z.number().int().min(0),
    teamsActive: z.number().int().min(0),
});
/**
 * Per-team contribution totals for the window, plus a commits-only sparkline
 * on the same buckets as the main series. Sorted by `commits` descending.
 */
export const metricTeamContributionsSchema = z.object({
    teamId: z.string().uuid(),
    teamName: z.string(),
    teamDisplayName: z.string(),
    pluginCount: z.number().int().min(0),
    commits: z.number().int().min(0),
    mergedPrs: z.number().int().min(0),
    contributors: z.number().int().min(0),
    /** Date-only (`YYYY-MM-DD`): the source column
     *  `analytics_daily_contributions.date_utc` is a `date`, so there is no
     *  time-of-day to report. Named `…Date` rather than `…At` to stop a
     *  consumer reading it as a timestamp. */
    lastCommitDate: z.string().date().nullable(),
    series: z.array(z.object({
        periodStart: z.string().date(),
        commits: z.number().int().min(0),
    })),
});
/**
 * One time-series bucket of the plugin catalog. Buckets come from
 * `plugins.created_at`; `cumulativePlugins` is its running total.
 *
 * `hubListedCumulative` is the running sum of `+1` per `plugin.hub_listed`
 * and `-1` per `plugin.hub_unlisted` audit row, restricted to audit rows
 * whose plugin is not soft-deleted — the same eligibility
 * `summary.pluginsListed` applies, which is what keeps the invariant below
 * true. `plugins.hub_listed_at` is
 * deliberately NOT the source: `hub-mirror.ts` nulls it when a plugin drops
 * out of the Hub manifest and resets it to `now()` when it returns, so it is
 * a current-state marker that would rewrite history on every mirror pass (it
 * feeds `summary.pluginsListed` only). The audit rows, by contrast, are
 * append-only and complete since the Hub bootstrap on 2026-05-27, when both
 * the column and the two actions landed in the same commit.
 *
 * The LATEST bucket must equal `summary.pluginsListed`.
 */
export const metricPluginsPointSchema = z.object({
    periodStart: z.string().date(),
    pluginsCreated: z.number().int().min(0),
    cumulativePlugins: z.number().int().min(0),
    /** Deliberately NOT `.min(0)`. The running total is the sum of an
     *  append-only audit stream; a negative value means that stream is damaged
     *  (an unlist with no matching list) and must reach the caller as the wrong
     *  number it is, rather than being clamped into a plausible zero. */
    hubListedCumulative: z.number().int(),
});
//# sourceMappingURL=metrics.js.map